import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postAdvisorUpdatesByAdvisorIdRequest,
  resetAdvisorSettings,
  selectAdvisorState,
  selectSessionAdvisor,
} from '..';
import { useToast } from '@client/components/hooks/use-toast';

const DEFAULT_HEALTHCARE_INFLATION_PERCENT = 5.4;
const DEFAULT_HEALTHCARE_INFLATION_SOURCE_TEXT =
  '2023 Center for Medicare & Medicaid Services (CMS) Office of the Actuary, National Health Expenditure Projections';
const DEFAULT_HEALTHCARE_INFLATION_SOURCE_URL =
  'https://www.cms.gov/newsroom/press-releases/cms-office-actuary-releases-2022-2031-national-health-expenditure-projections';
const DEFAULT_SCHEDULING_LINK_DISPLAY_TEXT = 'Connect with an advisor';

export const advisorRequiredFields: (keyof AdvisorProfile)[] = [
  'organizationName',
  'organizationDisplayName',
  'advisorFirstName',
  'advisorLastName',
  'advisorEmail',
];

const withAdvisorProfileDefaults = (profile: AdvisorProfile): AdvisorProfile => {
  return {
    ...profile,
    defaultHealthcareInflationPercent:
      profile.defaultHealthcareInflationPercent ?? DEFAULT_HEALTHCARE_INFLATION_PERCENT,
    defaultHealthcareInflationSourceText:
      profile.defaultHealthcareInflationSourceText ?? DEFAULT_HEALTHCARE_INFLATION_SOURCE_TEXT,
    defaultHealthcareInflationSourceUrl:
      profile.defaultHealthcareInflationSourceUrl ?? DEFAULT_HEALTHCARE_INFLATION_SOURCE_URL,
    schedulingLinkDisplayText:
      profile.schedulingLinkDisplayText ?? DEFAULT_SCHEDULING_LINK_DISPLAY_TEXT,
    advisorBusinessAddress: profile.advisorBusinessAddress,
    notificationPreference: (profile.notificationPreference ?? 'allow') as NotificationPreference,
  };
};

interface UseAdvisorProfileProps {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

export function useAdvisorProfile({
  hasUnsavedChanges,
  setHasUnsavedChanges,
}: UseAdvisorProfileProps) {
  const advisor = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();
  const {
    advisorSettings: { isLoading, status },
  } = useSelector(selectAdvisorState);
  const { toast } = useToast();

  const [advisorState, setAdvisorState] = useState<AdvisorProfile>(() => {
    try {
      return withAdvisorProfileDefaults(advisor);
    } catch (error) {
      console.error('Error applying advisor defaults:', error);
      return advisor;
    }
  });

  useEffect(() => {
    if (status === 'complete' && isLoading === false) {
      toast({
        title: 'Successfully saved!',
        description: 'Your changes have been saved successfully.',
        variant: 'default',
        className: '-translate-y-2 border-green-500 bg-green-50 text-green-800',
        duration: 5000,
      });
      dispatch(resetAdvisorSettings());
    }
  }, [isLoading, status, toast, dispatch]);

  const resetState = () => {
    try {
      setAdvisorState(withAdvisorProfileDefaults(advisor));
    } catch (error) {
      console.error('Error applying advisor defaults on reset:', error);
      setAdvisorState(advisor);
    }
    setHasUnsavedChanges(false);
  };

  const saveAdvisorProfile = (
    validationFn: (profile: AdvisorProfile) => { errors: any; errorMessages: string },
  ) => {
    if (!hasUnsavedChanges) {
      return { success: false, errors: {}, errorMessage: null };
    }

    const { advisorBusinessAddress, ...advisorProfile } = advisorState;
    let advisorBusinessAddressToSend: AdvisorAddress | undefined = undefined;

    if (advisorBusinessAddress) {
      const hasAddressData = Object.values(advisorBusinessAddress).some(
        value => value && typeof value === 'string' && value.trim() !== '',
      );

      if (hasAddressData) {
        advisorBusinessAddressToSend = {
          advisorId: advisor.advisorId,
          addressType: 'business' as const,
          streetAddress: advisorBusinessAddress.streetAddress ?? '',
          apartment: advisorBusinessAddress.apartment ?? '',
          city: advisorBusinessAddress.city ?? '',
          state: advisorBusinessAddress.state ?? '',
          zipCode: advisorBusinessAddress.zipCode ?? '',
          country: advisorBusinessAddress.country ?? 'US',
        };
      }
    }

    const advisorProfileToSend = {
      ...advisorProfile,
      advisorBusinessAddress: advisorBusinessAddressToSend,
    } as AdvisorProfile;

    const { errors, errorMessages } = validationFn(advisorProfileToSend);

    if (Object.values(errors).some(error => error)) {
      return { success: false, errors, errorMessage: errorMessages };
    }

    dispatch(postAdvisorUpdatesByAdvisorIdRequest(advisorProfileToSend));
    setHasUnsavedChanges(false);
    return { success: true, errors: {}, errorMessage: null };
  };

  const missingRequiredFields = advisorRequiredFields.filter(field => !advisor[field]);

  return {
    advisorState,
    setAdvisorState,
    resetState,
    saveAdvisorProfile,
    missingRequiredFields,
    isLoading,
    status,
  };
}
